import React from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar } from '@swyftx/aviary/atoms/Avatar';
import { Chip } from '@swyftx/aviary/atoms/Chip';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Heading } from '@swyftx/aviary/atoms/Typography';
import { VerifiedFilled } from '@swyftx/aviary/icons/filled';
import { CrossInCircle } from '@swyftx/aviary/icons/outlined';

import { UserStore } from '@shared/store';

import { observer } from 'mobx-react-lite';
import { useMarginAccount } from 'src/lib/margin/hooks/account';

type Props = {
  showAvatar?: boolean;
};

const SwyftxMarginAppHeaderProfileDetails: React.FC<Props> = observer(({ showAvatar = false }) => {
  const { userProfile, isUserVerified } = UserStore.useUserStore;
  const { account } = useMarginAccount();
  const { t } = useTranslation('margin');

  return (
    <FlexLayout direction='column' spacing={8}>
      <FlexLayout spacing={12} className='rounded-8 pt-8'>
        {showAvatar && <Avatar size='lg' text={userProfile?.name.first?.charAt(0)} />}
        <FlexLayout direction='column'>
          <Heading size='h5'>
            {userProfile?.name.first} {userProfile?.name.last}
          </Heading>
          <Body color='secondary' size='small' weight='emphasis'>
            {userProfile?.email}
          </Body>
        </FlexLayout>
      </FlexLayout>
      <FlexLayout spacing={8}>
        <Chip
          size='sm'
          color={isUserVerified() ? 'success' : 'destructive'}
          leadingIcon={
            isUserVerified() ? <VerifiedFilled className='h-16 w-16 text-color-text-success' /> : <CrossInCircle />
          }
          variant='subtle'
        >
          {isUserVerified() ? 'Verified' : 'Unverified'}
        </Chip>
        {account?.leverage && (
          <Chip variant='subtle' color='info' size='sm' bodyProps={{ color: 'accent' }}>
            {t('profile.labels.wholesale', { leverage: account.leverage })}
          </Chip>
        )}
      </FlexLayout>
    </FlexLayout>
  );
});

SwyftxMarginAppHeaderProfileDetails.displayName = 'SwyftxMarginAppHeaderProfileDetails';

export { SwyftxMarginAppHeaderProfileDetails };
